import { AtsCareerSiteBackgroundTypeEnum } from '@/generated/graphql/types';
import { LazyImage } from '@crehana/compass.ui';
import { Button } from '@crehana/compass.ui/button';
import clsx from 'clsx';
import { PREVIEW_TABLE } from '../constants';
import type { TCareerSiteHeaderProps } from '../types';

/**
 * CareerSiteHeader Component
 *
 * A customizable header component for career site pages that supports:
 * - Logo display with configurable positioning
 * - Background customization (solid color or image)
 * - Title and description text
 * - Optional call-to-action button
 * - Rounded corners (top only when specified)
 *
 * @param props - The component props
 * @returns JSX element representing the career site header
 */
export const CareerSiteHeader: React.FC<TCareerSiteHeaderProps> = ({
  className,
  logoUrl,
  logoEnabled,
  backgroundType,
  backgroundColor,
  backgroundImage,
  title,
  titleClassName,
  descriptionEnabled,
  description,
  descriptionClassName,
  textColor,
  buttonEnabled,
  buttonText,
  buttonColor,
  previewType,
  onClick,
}: TCareerSiteHeaderProps) => {
  return (
    <div
      className={clsx(
        'tw-w-full tw-flex tw-flex-col tw-gap-2.4',
        // Only apply default padding if no custom className is provided
        !className &&
          (previewType === PREVIEW_TABLE.DESKTOP || !previewType) &&
          'tw-px-[9.2rem]',
        !className && previewType === PREVIEW_TABLE.MOBILE && 'tw-px-3.2',
        // Apply custom className if provided
        className,
      )}
      style={
        // Dynamic background styling based on background type
        backgroundType ===
          AtsCareerSiteBackgroundTypeEnum.CareerSiteBackgroundImage &&
        backgroundImage
          ? {
              backgroundImage: `url(${typeof backgroundImage === 'string' ? backgroundImage : backgroundImage?.url})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }
          : {
              background: backgroundColor
                ? backgroundColor.primaryColor
                : '#F0F2FF',
            }
      }
    >
      {/* Logo section - conditionally rendered based on logoEnabled prop */}
      {logoEnabled && logoUrl?.url && (
        <div className="tw-w-[10.7rem] tw-h-[5.6rem] tw-flex tw-justify-start">
          <LazyImage
            src={logoUrl?.url || ''}
            alt={title || ''}
            className="tw-w-full tw-h-full"
            imgClassName="tw-object-contain tw-object-left tw-w-full tw-h-full"
          />
        </div>
      )}
      {/* Text content section - title and optional description */}
      <div className="tw-flex tw-flex-col tw-gap-0.8">
        {/* Main title with dynamic text color */}
        <div
          className={clsx(
            'tw-whitespace-pre-line',
            titleClassName || 'tw-font-h3 tw-font-semibold',
          )}
          style={{ color: textColor?.primaryColor }}
        >
          {title}
        </div>
        {/* Optional description text */}
        {descriptionEnabled && description && (
          <div
            className={clsx(
              'tw-whitespace-pre-line',
              descriptionClassName || 'tw-font-body4',
            )}
            style={{ color: textColor?.primaryColor }}
          >
            {description}
          </div>
        )}
      </div>
      {/* Optional call-to-action button */}
      {buttonEnabled && buttonText && (
        <Button
          type="button"
          variant="primary"
          className={clsx(
            'tw-btn-primary-xs tw-w-fit',
            previewType === PREVIEW_TABLE.MOBILE &&
              'tw-w-full tw-flex tw-justify-center',
          )}
          style={{
            background: buttonColor?.primaryColor,
          }}
          onClick={() => {
            onClick?.();
          }}
        >
          {buttonText}
        </Button>
      )}
    </div>
  );
};
